import React, { useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { App, type CatalogState } from "./App";
import { CartProvider } from "./cart";
import { fetchCatalog } from "./catalog";
import { fetchRuntimeConfig, otelNamespace } from "./config";
import { CustomerProvider } from "./customer";
import { fetchCustomers, type Customer } from "./customers";
import { initBrowserOtel } from "./otel";
import { loadSession, type Me, type SessionState } from "./session";
import { SignInScreen } from "./SignInScreen";
import "./styles.css";

const el = document.getElementById("root");
if (!el) throw new Error("#root missing");

function Root() {
  const [session, setSession] = useState<SessionState | null>(null);
  const [catalog, setCatalog] = useState<CatalogState>({ status: "loading" });
  const [customers, setCustomers] = useState<Customer[]>([]);

  useEffect(() => {
    void loadSession().then(setSession);
  }, []);

  useEffect(() => {
    if (session?.kind !== "signed-in") return;
    fetchCatalog()
      .then((products) => setCatalog({ status: "ready", products }))
      .catch((err: unknown) => setCatalog({ status: "error", message: String(err) }));
    fetchCustomers()
      .then(setCustomers)
      .catch(() => setCustomers([]));
  }, [session?.kind]);

  if (session === null) {
    return <p className="status-message">Loading…</p>;
  }
  if (session.kind !== "signed-in") {
    return <SignInScreen state={session} />;
  }

  // The signed-in identity as a checkout option (#304) — order-service
  // auto-upserts a profile row for it on login regardless (ADR 0059), so
  // it needs no seeding. tier/creditLimit are display-only in this app
  // (never read past CustomerPicker/CartDrawer); the real credit check is
  // server-side in payment-service and simply no-ops for an unseeded id.
  const self: Customer = {
    customerId: session.me.sub,
    name: session.me.name,
    tier: "you",
    creditLimit: 0,
  };

  return (
    <CustomerProvider self={self} personas={customers}>
      <CartProvider products={catalog.status === "ready" ? catalog.products : []}>
        <App catalog={catalog} me={session.me} />
      </CartProvider>
    </CustomerProvider>
  );
}

export type { Me };

// config.json (OTel namespace) before first render, then the web tracer
// (#126, ADR 0031/0036) — spans export to the relative `/v1/traces`, which
// the BFF forwards. The checkout POST carries `traceparent`, so browse →
// cart → checkout is one trace.
void fetchRuntimeConfig().then(() => {
  initBrowserOtel(otelNamespace());
  createRoot(el).render(
    <React.StrictMode>
      <Root />
    </React.StrictMode>,
  );
});
